// functionTriggers.js
// Client writes to `users/{company_id}/function_triggers` — the documents the
// Automations tab presents as Automations.
//
// THE INVARIANT: every configuration change to an Automation — its schedule, its
// source table, its parameters, its type, its enabled flag — disables Email on
// that Automation in the SAME write, alongside the audit fields recording who did
// it and when. The publish prompt may explicitly restore a previous permission
// after reviewing the resulting version, unchanged filters and recipients.
// Otherwise, turning Email back on requires a separate confirmation against the
// saved configuration. That is why configuration edits go through
// `commitTriggerConfig` and never through a bare `updateDoc`: a caller that
// forgets the joint commit leaves an Automation authorised to send email under a
// configuration nobody approved. `renameAutomation` is the ONE exemption — a
// display name changes nothing about what runs.
//
// THE PIN INVARIANT: an enabled Automation's `function_version` always equals its
// Function's `published_version`. Publishing a new version disables the
// Function's user-created automations in the same backend transaction, and the
// publish dialog offers to migrate and re-pin them; enabling from the tab
// migrates a stale pin itself. A missing or malformed pin counts as stale
// everywhere: both dispatchers refuse to fire an automation whose pin does not
// match the head.
//
// The document shape produced by `buildTriggerDoc` is the ONE shape: the
// Automations tab and the View's "Keep this View current" panel both create
// automations through it, so an automation cannot arrive in the collection
// missing fields the backend scheduler expects.
//
// Every query here is single-field (`where('managed_by.view_id', '==', …)`) or a
// document-id read, so none of this needs a composite index.

import { assertResourceAvailable } from '../utils/resourceLifecycle';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { db } from './firebase';
import {
  isPublishedVersion,
  PUBLISH_ACTOR,
  versionDataSourceBlocks,
} from './functionVersions';
import { cleanRangeValue, sameRunParameters, isDateRangeParam, isStartDateParam } from '../utils/functionParameterUtils';
import { normalizeCategoryType } from '../utils/tagValueUtils';
import { resolveViewModel, widgetInstanceIds } from '../utils/widgetModel';
import { stableStringify } from '../utils/stableJson';

const triggerRef = (companyId, triggerId) => doc(
  db,
  `users/${companyId}/function_triggers`,
  triggerId,
);

const functionRef = (companyId, functionId) => doc(
  db,
  `users/${companyId}/data_functions`,
  functionId,
);

const versionRef = (companyId, functionId, version) => doc(
  db,
  `users/${companyId}/data_functions/${functionId}/versions/${version}`,
);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// A daily 09:00 Europe/London schedule. `next_run_at: null` asks the backend
// scheduler to recompute the next firing time from the rest of the config —
// carrying a stale one forward would fire the trigger at the old time once.
export const DEFAULT_SCHEDULE_CONFIG = {
  frequency: 'daily',
  day_of_week: 0,
  day_of_month: 1,
  hour: 9,
  minute: 0,
  timezone: 'Europe/London',
  next_run_at: null,
};

// Persist the same days the schedule editor displays. Daily schedules can
// legitimately carry null for unused day fields; changing frequency resolves
// those choices to Monday or the first day of the month.
export function scheduleForSave(schedule) {
  const config = {
    ...DEFAULT_SCHEDULE_CONFIG,
    ...schedule,
    day_of_week: schedule?.day_of_week ?? DEFAULT_SCHEDULE_CONFIG.day_of_week,
    day_of_month: schedule?.day_of_month ?? DEFAULT_SCHEDULE_CONFIG.day_of_month,
    next_run_at: null,
  };
  if (config.frequency === 'weekly'
      && (!Number.isInteger(config.day_of_week) || config.day_of_week < 0 || config.day_of_week > 6)) {
    throw new Error('Choose a weekday from Monday to Sunday.');
  }
  if (config.frequency === 'monthly'
      && (!Number.isInteger(config.day_of_month) || config.day_of_month < 1 || config.day_of_month > 28)) {
    throw new Error('Choose a day of the month between 1 and 28.');
  }
  return config;
}

// A display name is metadata, so it is capped rather than validated: one
// pathological paste must not be able to break the list layout.
const NAME_MAX_LENGTH = 100;

// The backend switches an automation off after this many consecutive failed
// runs, counted in `run_failures` (data_functions/triggers.py MAX_RUN_FAILURES).
// Every write here that saves a new configuration or switches an automation on
// returns that count to 0: the failures counted belong to what was replaced.
export const MAX_RUN_FAILURES = 3;

export const newTriggerId = () => `trigger_${nanoid(10)}`;

// An abort the caller is expected to explain rather than treat as a failure:
// the Function moved under the write, or is no longer runnable at all.
const abort = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

// Trimmed, defaulted, capped. An automation with no name of its own is named
// after its Function, and one whose Function has no name either falls back to
// its own id so a row can never render blank.
const automationName = (name, functionName, triggerId) => {
  const trimmed = String(name || '').trim();
  const functionLabel = String(functionName || '').trim();
  const fallback = functionLabel ? `${functionLabel} automation` : String(triggerId || '');
  return (trimmed || fallback).slice(0, NAME_MAX_LENGTH);
};

// Accepts either raw version blocks (`block.config.filters`) or the flattened
// shape `versionDataSourceBlocks` returns (`block.filters`).
const blockFilters = (block) => block?.config?.filters || block?.filters || [];

// The type a filter's values are compared and rendered as. Mirrors the rule
// `buildDefaultTriggerParameters` applies when it builds a default value.
const filterType = (filter) => normalizeCategoryType(
  filter?.type || (isDateRangeParam(filter?.column) ? 'Date' : 'Text'),
);

// The parameters a newly created Automation starts with, derived from the
// data-source blocks of the version it will execute.
//
// Date parameters default to DYNAMIC tokens rather than to the fixed dates the
// version happens to carry: an automation that fires every month with
// `start_date` pinned to the day it was created reports on the wrong month
// forever. Non-date filters keep the version's own values, which are already the
// defaults the author chose.
//
// Mirrored in Python by `default_trigger_parameters` (data_functions/triggers.py)
// — the two implementations must agree, so they share a fixture set.
//
// Accepts either raw version blocks (`block.config.filters`) or the flattened
// shape `versionDataSourceBlocks` returns (`block.filters`), because both
// callers have one of the two in hand.
export function buildDefaultTriggerParameters(dataSourceBlocks) {
  const parameters = {};
  if (!dataSourceBlocks) return parameters;

  Object.entries(dataSourceBlocks).forEach(([blockId, block]) => {
    const blockParams = {};
    const blockRangeFilters = {};
    const filters = blockFilters(block);

    filters.forEach((filter) => {
      if (!filter?.variable) return;
      if (filter.range) {
        const cleaned = cleanRangeValue(filter.range);
        if (Object.keys(cleaned).length > 0) {
          blockRangeFilters[filter.column] = cleaned;
        }
        return;
      }
      const paramType = filterType(filter);
      if (paramType === 'Date') {
        if (!isDateRangeParam(filter.column)) throw new Error(`${filter.column} requires a Date range; migrate its saved filter.`);
        blockParams[filter.column] = isStartDateParam(filter.column)
          ? 'dynamic:start_of_month'
          : 'dynamic:today';
      } else {
        blockParams[filter.column] = Array.isArray(filter.value)
          ? filter.value
          : (filter.value ? [filter.value] : []);
      }
    });

    if (Object.keys(blockRangeFilters).length > 0) {
      blockParams.range_filters = blockRangeFilters;
    }
    if (Object.keys(blockParams).length > 0) {
      parameters[blockId] = blockParams;
    }
  });

  return parameters;
}

// Carry an Automation's saved parameters onto a new version of its Function.
//
// Trigger parameters are keyed by data-source block id, so a version that
// restructures its blocks silently ignores parameters it no longer recognises —
// an unfiltered nightly report. Every saved value is therefore re-checked
// against the new version: a column that is no longer a variable filter, that
// changed mode (a plain value on a column now configured as a range is rejected
// outright by the runner), or whose type changed is DROPPED rather than carried
// across, and the drop is reported so the surfaces can name the filters that
// no longer apply. Omitted values never acquire a new version's defaults.
//
// `oldVersionBlocks` is the previously pinned version's blocks, loaded
// best-effort: when it is unavailable the type comparison is skipped and only
// mode compatibility is applied.
export function migrateTriggerParameters(oldParameters, newVersionBlocks, oldVersionBlocks = null) {
  // Defaults hydrate a new form only. An omitted saved parameter is an
  // intentional absence of a constraint, including after a republish.
  const parameters = {};
  const dropped = [];
  const oldParams = isPlainObject(oldParameters) ? oldParameters : {};

  Object.entries(oldParams).forEach(([blockId, blockValues]) => {
    if (!isPlainObject(blockValues)) return;
    const newFilters = blockFilters(newVersionBlocks?.[blockId]);
    const oldFilters = oldVersionBlocks ? blockFilters(oldVersionBlocks[blockId]) : null;

    // A saved value survives only where the same column is still a variable
    // filter of the same mode, and — when the old version is readable — of the
    // same type.
    const survives = (column, wantsRange) => {
      const newFilter = newFilters.find((filter) => (
        filter?.column === column
        && Boolean(filter?.variable)
        && Boolean(filter?.range) === wantsRange
      ));
      if (!newFilter) return false;
      const oldFilter = oldFilters
        ? oldFilters.find((filter) => filter?.column === column)
        : null;
      if (!oldFilter) return true;
      return filterType(oldFilter) === filterType(newFilter);
    };

    Object.entries(blockValues).forEach(([key, value]) => {
      if (key === 'range_filters') {
        if (!isPlainObject(value)) return;
        Object.entries(value).forEach(([column, rangeValue]) => {
          if (!survives(column, true)) {
            dropped.push({ blockId, column });
            return;
          }
          const blockParams = parameters[blockId] || {};
          parameters[blockId] = {
            ...blockParams,
            range_filters: { ...(blockParams.range_filters || {}), [column]: rangeValue },
          };
        });
        return;
      }
      if (key === 'exclude_filters') {
        // A NOT filter carries over exactly as an IS one does. A column is
        // included or excluded, never both.
        if (!isPlainObject(value)) return;
        Object.entries(value).forEach(([column, excludedValue]) => {
          if (!survives(column, false)) {
            dropped.push({ blockId, column });
            return;
          }
          const { [column]: seeded, ...blockParams } = parameters[blockId] || {};
          parameters[blockId] = {
            ...blockParams,
            exclude_filters: { ...(blockParams.exclude_filters || {}), [column]: excludedValue },
          };
        });
        return;
      }
      if (!survives(key, false)) {
        dropped.push({ blockId, column: key });
        return;
      }
      parameters[blockId] = { ...(parameters[blockId] || {}), [key]: value };
    });
  });

  return { parameters, dropped };
}

// One Automation document, complete. `managedBy` marks an Automation some other
// surface owns and may therefore remove again — a View's coverage panel deletes
// only its own; one a person created carries no such mark and is never touched.
export function buildTriggerDoc({
  triggerId,
  functionId,
  functionName,
  name = '',
  triggerType,
  tableId = null,
  schedule = null,
  parameters = {},
  functionVersion = null,
  actorUid,
  managedBy = null,
}) {
  const now = serverTimestamp();
  return {
    trigger_id: triggerId,
    function_id: functionId,
    function_name: functionName || functionId,
    name: automationName(name, functionName, triggerId),
    // The published version this automation is configured against. Enabled
    // implies it equals the Function head's `published_version`.
    function_version: functionVersion,
    enabled: true,
    trigger_type: triggerType,
    on_new_data_config: triggerType === 'on_new_data'
      ? { table_id: tableId, data_profile_id: null }
      : null,
    on_schedule_config: triggerType === 'on_schedule'
      ? scheduleForSave(schedule)
      : null,
    parameters,
    // A new Automation never sends Email until Email is explicitly enabled on it.
    allow_email: false,
    email_permission_updated_at: now,
    email_permission_updated_by: actorUid,
    last_run_at: null,
    last_run_status: null,
    last_run_id: null,
    run_failures: 0,
    created_at: now,
    updated_at: now,
    ...(managedBy ? { managed_by: managedBy } : null),
  };
}

// A View-managed Automation is also a dependency of its View. Keep this read
// in the save transaction; a plain preflight read leaves a deletion race.
async function assertAutomationViewAvailable(transaction, companyId, trigger) {
  if (trigger.managed_by?.type !== 'view') return;
  const viewRef = doc(db, `users/${companyId}/dashboard_views`, trigger.managed_by.view_id);
  assertResourceAvailable(await transaction.get(viewRef), 'View');
}

// Create an Automation against a version the caller has already read. The
// transaction re-reads the Function head and aborts when the published version
// moved (or the Function became invalid, or was deleted) in between, because the
// document's pin and its default parameters both describe the version the caller
// saw — writing them against a newer head would create an automation that is
// stale the moment it exists.
export async function createAutomation(companyId, triggerDoc, expectedVersion) {
  const headRef = functionRef(companyId, triggerDoc.function_id);
  return runTransaction(db, async (tx) => {
    const headSnapshot = await tx.get(headRef);
    if (!headSnapshot.exists()) {
      throw abort('function_missing', 'This function no longer exists.');
    }
    assertResourceAvailable(headSnapshot, 'Function');
    await assertAutomationViewAvailable(tx, companyId, triggerDoc);
    const head = headSnapshot.data() || {};
    if (head.invalid === true) {
      throw abort('function_invalid', head.invalid_reason || 'This function references a resource that no longer exists.');
    }
    if (!isPublishedVersion(expectedVersion) || head.published_version !== expectedVersion) {
      throw abort('version_moved', 'This Function was just republished — reopen the picker.');
    }
    tx.set(triggerRef(companyId, triggerDoc.trigger_id), triggerDoc);
    return { id: triggerDoc.trigger_id, ...triggerDoc };
  });
}

export async function deleteTrigger(companyId, triggerId) {
  await deleteDoc(triggerRef(companyId, triggerId));
}

// ---- View-managed automations: one per View CARD ----------------------------
//
// A View's "Keep current" dialog configures one automation per card (widget
// instance), tagged `managed_by: { type: 'view', view_id, view_instance_id }`.
// The two dispatchers stamp that pair onto every run the automation starts, so
// the View adopts the run by card identity (utils/viewCards.js). The document
// id is deterministic per card: a second create for the same card — two tabs,
// a reconcile racing a save — lands on the same document instead of beside it,
// so duplicates are impossible rather than policed.

export const viewTriggerId = (viewId, instanceId) => `trigger_view_${viewId}_${instanceId}`;

const CONFIG_FIELDS = ['trigger_type', 'on_schedule_config', 'on_new_data_config', 'parameters'];

// An editor compares configuration, not the scheduler's changing run receipts.
// This is an in-memory opening snapshot; no revision field is stored.
export function automationConfigSnapshot(trigger, { includeName = true } = {}) {
  if (!trigger) return null;
  const { next_run_at, ...schedule } = trigger.on_schedule_config || {};
  const permissionTime = trigger.email_permission_updated_at;
  return {
    ...(includeName ? { name: trigger.name || '' } : {}),
    function_id: trigger.function_id ?? null,
    function_version: trigger.function_version ?? null,
    enabled: trigger.enabled === true,
    trigger_type: trigger.trigger_type ?? null,
    parameters: trigger.parameters ?? {},
    on_schedule_config: trigger.on_schedule_config ? schedule : null,
    on_new_data_config: trigger.on_new_data_config ?? null,
    managed_by: trigger.managed_by ?? null,
    allow_email: trigger.allow_email === true,
    email_permission_updated_by: trigger.email_permission_updated_by ?? null,
    email_permission_updated_at: typeof permissionTime?.toMillis === 'function'
      ? permissionTime.toMillis()
      : (permissionTime instanceof Date ? permissionTime.getTime() : permissionTime ?? null),
  };
}

// The writes that reconcile a View's own automations to `plans`, one plan per
// card the dialog saved:
//   { instanceId, functionId, functionName, name, triggerType, tableId,
//     schedule, parameters, functionVersion, enabled }
// A plan switched off deletes the card's automation; a plan switched on creates
// it (a full document through buildTriggerDoc) or, when `existingById` holds
// the card's document, updates only its configuration — so the dispatcher's
// `last_run_*` receipts survive a re-save. Every configuration write applies
// THE INVARIANT. `deleteIds` are the caller's strays.
export function planViewAutomationWrites({ viewId, plans, existingById = {}, deleteIds = [], actorUid }) {
  const now = serverTimestamp();
  const writes = [];
  (plans || []).forEach((plan) => {
    const id = viewTriggerId(viewId, plan.instanceId);
    const existing = existingById[id] || null;
    if (!plan.enabled) {
      if (existing) writes.push({ op: 'delete', id });
      return;
    }
    const managedBy = { type: 'view', view_id: viewId, view_instance_id: plan.instanceId };
    const doc = buildTriggerDoc({
      triggerId: id,
      functionId: plan.functionId,
      functionName: plan.functionName,
      name: plan.name,
      triggerType: plan.triggerType,
      tableId: plan.triggerType === 'on_new_data' ? (plan.tableId || null) : null,
      schedule: plan.schedule,
      parameters: plan.parameters,
      functionVersion: plan.functionVersion,
      actorUid,
      managedBy,
    });
    if (!existing) {
      writes.push({ op: 'set', id, doc });
      return;
    }
    const updates = {
      name: doc.name,
      function_id: doc.function_id,
      function_name: doc.function_name,
      function_version: doc.function_version,
      managed_by: managedBy,
      enabled: true,
      disabled_reason: null,
      dispatch_failures: 0,
      last_dispatch_error: null,
      run_failures: 0,
      publish_sweep: deleteField(),
      allow_email: false,
      email_permission_updated_at: now,
      email_permission_updated_by: actorUid,
      updated_at: now,
    };
    CONFIG_FIELDS.forEach((field) => { updates[field] = doc[field]; });
    writes.push({ op: 'update', id, updates });
  });
  (deleteIds || []).forEach((id) => {
    if (!writes.some((write) => write.id === id)) writes.push({ op: 'delete', id });
  });
  return writes;
}

// Save changed cards atomically. A submitted opening snapshot is required for
// each card so a concurrent edit or switch-off cannot be overwritten. Cleanup
// separately verifies membership in the CURRENT saved View, never the browser's
// older card list. All reads and checks precede the first write.
export async function saveViewAutomations(companyId, {
  viewId, plans = [], deleteIds = [], expectedConfigs = {}, actorUid,
}) {
  const enabledPlans = plans.filter((plan) => plan.enabled);
  const functionIds = Array.from(new Set(enabledPlans.map((plan) => plan.functionId)));
  const plannedIds = new Set(plans.map((plan) => viewTriggerId(viewId, plan.instanceId)));
  return runTransaction(db, async (tx) => {
    const viewSnapshot = await tx.get(doc(db, `users/${companyId}/dashboard_views`, viewId));
    if (!viewSnapshot.exists()) throw abort('view_missing', 'This view no longer exists.');
    assertResourceAvailable(viewSnapshot, 'View');
    const currentView = viewSnapshot.data();
    const present = new Set(widgetInstanceIds(resolveViewModel(currentView).widgets));
    const functionForCard = (instanceId) => currentView.functionInstanceMap?.[instanceId] || instanceId;
    // Read other cards of the edited Functions for the existing duplicate-run
    // check. They are never written, and their latest on/off state participates.
    const relatedIds = [...present].filter((instanceId) => functionIds.includes(functionForCard(instanceId)))
      .map((instanceId) => viewTriggerId(viewId, instanceId));
    const readIds = Array.from(new Set([...plannedIds, ...deleteIds, ...relatedIds]));
    const headSnapshots = await Promise.all(functionIds.map((id) => tx.get(functionRef(companyId, id))));
    const triggerSnapshots = await Promise.all(readIds.map((id) => tx.get(triggerRef(companyId, id))));
    const existingById = {};
    triggerSnapshots.forEach((snapshot, index) => {
      if (snapshot.exists()) existingById[readIds[index]] = snapshot.data();
    });
    const owned = (trigger) => trigger.managed_by?.type === 'view' && trigger.managed_by.view_id === viewId;

    for (const plan of plans) {
      const id = viewTriggerId(viewId, plan.instanceId);
      const current = existingById[id] || null;
      const label = plan.cardLabel || plan.name || plan.functionName || plan.instanceId;
      if (current && (!owned(current) || current.managed_by.view_instance_id !== plan.instanceId)) {
        throw abort('automation_not_owned', `${label}: this automation no longer belongs to this View card.`);
      }
      if (!Object.prototype.hasOwnProperty.call(expectedConfigs, id)
        || stableStringify(automationConfigSnapshot(current)) !== stableStringify(expectedConfigs[id])) {
        throw abort('automation_changed', `${label} changed while Keep current was open. Close it and open it again to review the current settings.`);
      }
      if (!plan.enabled) continue;
      const functionId = functionForCard(plan.instanceId);
      if (!present.has(plan.instanceId) || functionId !== plan.functionId) {
        throw abort('view_changed', `${label} was removed or its Function changed. Reload the View before saving.`);
      }
      const headSnapshot = headSnapshots[functionIds.indexOf(plan.functionId)];
      if (!headSnapshot.exists()) throw abort('function_missing', `${plan.functionName} no longer exists.`);
      assertResourceAvailable(headSnapshot, 'Function');
      const head = headSnapshot.data();
      if (head.invalid === true) {
        throw abort('function_invalid', head.invalid_reason || `${plan.functionName} references a deleted resource.`);
      }
      if (!isPublishedVersion(plan.functionVersion) || head.published_version !== plan.functionVersion) {
        throw abort('version_moved', `${label} was just republished — close and reopen Keep current.`);
      }
    }

    const effective = Object.fromEntries(Object.entries(existingById).filter(([id, trigger]) => (
      owned(trigger) && id === viewTriggerId(viewId, trigger.managed_by.view_instance_id)
    )));
    plans.forEach((plan) => {
      effective[viewTriggerId(viewId, plan.instanceId)] = {
        enabled: plan.enabled, function_id: plan.functionId,
        function_version: plan.functionVersion, parameters: plan.parameters,
      };
    });
    for (const plan of enabledPlans) {
      const id = viewTriggerId(viewId, plan.instanceId);
      const duplicate = relatedIds.find((otherId) => otherId !== id
        && effective[otherId]?.enabled === true
        && effective[otherId].function_id === plan.functionId
        && effective[otherId].function_version === plan.functionVersion
        && sameRunParameters(effective[otherId].parameters, plan.parameters));
      if (duplicate) {
        throw abort('duplicate_automation', `${plan.cardLabel || plan.name} would duplicate another enabled card's automation. Change its filters or switch one off.`);
      }
    }

    const confirmedDeletes = [];
    for (const id of new Set(deleteIds)) {
      if (plannedIds.has(id) || !existingById[id]) continue;
      const current = existingById[id];
      if (!owned(current)) throw abort('automation_not_owned', 'This automation no longer belongs to this View.');
      const cardIsOnTheView = present.has(current.managed_by.view_instance_id);
      if (!cardIsOnTheView) confirmedDeletes.push(id);
    }
    const writes = planViewAutomationWrites({
      viewId, plans, existingById, deleteIds: confirmedDeletes, actorUid,
    });
    writes.forEach((write) => {
      const ref = triggerRef(companyId, write.id);
      if (write.op === 'delete') tx.delete(ref);
      else if (write.op === 'set') tx.set(ref, write.doc);
      else tx.update(ref, write.updates);
    });
    return { writes: writes.length };
  });
}

// The Functions whose published version emails `contactId` — by the head's
// `email_contact_ids`, written at publish — and how many automations run
// them: what an address change or a deletion of that contact reaches, named
// in Settings before the edit is made. One array-contains query on a single
// field; the automations are the full listing filtered in memory.
export async function contactEmailUsage(companyId, contactId) {
  const snapshot = await getDocs(query(
    collection(db, `users/${companyId}/data_functions`),
    where('email_contact_ids', 'array-contains', contactId),
  ));
  const functions = snapshot.docs.map((functionDoc) => ({
    id: functionDoc.id,
    name: functionDoc.data()?.name || functionDoc.id,
  }));
  if (functions.length === 0) return { functions, automations: 0 };
  const functionIds = new Set(functions.map((entry) => entry.id));
  const automations = (await listAutomations(companyId))
    .filter((trigger) => functionIds.has(trigger.function_id)).length;
  return { functions, automations };
}

// Every Automation in the company. The tab lists them all, across Functions, so
// this is a full-collection read rather than a per-Function query.
export async function listAutomations(companyId) {
  const snapshot = await getDocs(collection(db, `users/${companyId}/function_triggers`));
  return snapshot.docs.map((triggerDoc) => ({ id: triggerDoc.id, ...triggerDoc.data() }));
}

// The Automations this View owns — and nothing else. The View coverage panel
// counts, toggles, and removes exactly this set, so one a person created can
// never be adopted, modified, or deleted by a View. The backend uses the same
// query when a View is deleted (delete_resource.py cascades on
// `managed_by.view_id`).
export function watchViewTriggers(companyId, viewId, onChange, onError) {
  return onSnapshot(query(
    collection(db, `users/${companyId}/function_triggers`),
    where('managed_by.view_id', '==', viewId),
  ), (snapshot) => onChange(snapshot.docs
    .map((triggerDoc) => ({ id: triggerDoc.id, ...triggerDoc.data() }))
    .filter((trigger) => trigger.managed_by?.type === 'view')), onError);
}

// The only way to change an Automation's configuration. See THE INVARIANT above:
// `allow_email` and its audit fields are part of the same update, never a
// follow-up write that could be lost.
//
// The sweep receipt goes in the same write: a person editing an automation a
// publish just switched off has answered the keep prompt with their hands, so a
// publish dialog still open elsewhere must lose its authority to switch that
// automation back on (and re-allow its email) behind them.
export async function commitTriggerConfig(companyId, triggerId, updates, actorUid) {
  const now = serverTimestamp();
  const ref = triggerRef(companyId, triggerId);
  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) throw abort('automation_missing', 'This automation no longer exists.');
    const head = await tx.get(functionRef(companyId, snapshot.data().function_id));
    assertResourceAvailable(head, 'Function');
    await assertAutomationViewAvailable(tx, companyId, snapshot.data());
    tx.update(ref, {
      ...updates,
      allow_email: false,
      email_permission_updated_at: now,
      email_permission_updated_by: actorUid,
      publish_sweep: deleteField(),
      updated_at: now,
    });
  });
}

// Enabling an Automation clears whatever disabled it and, for a schedule, drops
// the stale next firing time so the backend recomputes it.
export function enableTriggerUpdates(trigger) {
  const updates = { enabled: true, disabled_reason: null };
  if (trigger?.trigger_type === 'on_schedule') {
    updates['on_schedule_config.next_run_at'] = null;
  }
  return updates;
}

// --- Editing an Automation: edit → lease → save -----------------------------
// The Automations tab edits an Automation in place. Its editor shows the live
// document read-only until Edit is pressed, which takes the document's
// `edit_lease` (services/editLease.js); the edits accumulate in a local draft
// of the fields below and land in ONE transactional save that requires the
// lease to still be this session's. Nothing is written between Edit and Save,
// so a half-made change — a date switched from Dynamic to Fixed with no date
// chosen yet — can never run.

// The fields an editing session owns. Everything else on the document — the
// on/off state, the email permission, the last-run receipt the dispatcher
// writes, the pin — is written by its own action or by the backend, and a save
// never touches it.
export const automationDraftOf = (trigger) => ({
  name: trigger?.name || '',
  parameters: isPlainObject(trigger?.parameters) ? trigger.parameters : {},
  on_schedule_config: trigger?.on_schedule_config || null,
  on_new_data_config: trigger?.on_new_data_config || null,
  // The version the parameters describe. The editor moves it to the version
  // the Function publishes now when it seeds a draft from an older pin (H85).
  function_version: trigger?.function_version ?? null,
});

// Whether two drafts configure the same run: the same parameters and the same
// schedule (its next firing time is the dispatcher's, not configuration) or
// watched table. The name is display only and is not configuration.
export const sameAutomationConfig = (a, b) => {
  const config = (draft) => {
    const { next_run_at, ...schedule } = draft?.on_schedule_config || {};
    return {
      schedule: draft?.on_schedule_config ? schedule : null,
      table_id: draft?.on_new_data_config?.table_id || null,
    };
  };
  return sameRunParameters(a?.parameters, b?.parameters)
    && stableStringify(config(a)) === stableStringify(config(b));
};

// A grant belongs to the configuration and published version shown when its
// confirmation opened. Run receipts and a display-name change do not change
// that consent; another configuration or permission write does. Revoking Email
// remains possible even when the automation is off or its Function is gone.
export async function setAutomationEmailPermission(
  companyId,
  triggerId,
  value,
  { actorUid, expectedVersion, reviewedConfig } = {},
) {
  const ref = triggerRef(companyId, triggerId);
  return runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) {
      throw abort('automation_missing', 'This automation no longer exists.');
    }
    const current = snapshot.data() || {};
    if (value === true) {
      if (!reviewedConfig || stableStringify(automationConfigSnapshot(current, { includeName: false }))
        !== stableStringify(reviewedConfig)) {
        throw abort('automation_changed', 'This automation or its Email permission changed while you were reviewing it. Review its current settings and allow Email again.');
      }
      if (current.managed_by || current.enabled !== true) {
        throw abort('email_unavailable', 'Email can only be allowed on an enabled, user-managed automation.');
      }
      const headSnapshot = await tx.get(functionRef(companyId, current.function_id));
      if (!headSnapshot.exists()) {
        throw abort('function_missing', 'This function no longer exists.');
      }
      assertResourceAvailable(headSnapshot, 'Function');
      const head = headSnapshot.data() || {};
      if (head.invalid === true) {
        throw abort('function_invalid', head.invalid_reason || 'This function references a resource that no longer exists.');
      }
      if (!isPublishedVersion(expectedVersion) || head.published_version !== expectedVersion
        || current.function_version !== expectedVersion) {
        throw abort('version_moved', 'This function was published again. Reload it and review its current Email Outputs before allowing Email.');
      }
    }
    const now = serverTimestamp();
    tx.update(ref, {
      allow_email: value === true,
      email_permission_updated_at: now,
      email_permission_updated_by: actorUid,
      publish_sweep: deleteField(),
      updated_at: now,
    });
  });
}

// The one configuration write of the editor. The transaction re-reads the
// document and refuses unless the edit lease is still `sessionId`'s — a
// session that lost the lease to a takeover has been editing a document
// someone else may since have saved, and its draft is not the one to keep.
// Only the draft's own field paths are written, so the dispatcher's
// `last_run_*`, `enabled` and `disabled_reason` written meanwhile survive; a
// configuration change (anything but the name) also applies THE INVARIANT.
// The lease is released in the same write.
export async function saveAutomationDraft(companyId, triggerId, draft, { sessionId, actorUid }) {
  const ref = triggerRef(companyId, triggerId);
  return runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) {
      throw abort('automation_missing', 'This automation no longer exists.');
    }
    const current = snapshot.data() || {};
    const lease = current.edit_lease || null;
    if (!lease || lease.session_id !== sessionId) {
      throw abort('lease_lost', 'Your editing session ended — another session took over this automation. Reopen it to edit.');
    }
    // A draft edited against a newer version than the document is pinned to
    // re-pins it in the same write, once the head is confirmed to still
    // publish that version: the saved values are then read back through the
    // contract they were written against, never migrated through an older
    // one (H85). Read before the first write, Firestore's rule.
    const functionSnapshot = await tx.get(functionRef(companyId, current.function_id));
    assertResourceAvailable(functionSnapshot, 'Function');
    await assertAutomationViewAvailable(tx, companyId, current);
    const pinTarget = isPublishedVersion(draft.function_version) && draft.function_version !== current.function_version
      ? draft.function_version
      : null;
    if (pinTarget) {
      const headSnapshot = await tx.get(functionRef(companyId, current.function_id));
      if (!headSnapshot.exists()) {
        throw abort('function_missing', 'This function no longer exists.');
      }
      if (headSnapshot.data()?.published_version !== pinTarget) {
        throw abort('version_moved', 'This function was published again while you were editing, so this draft describes a version it no longer runs. Reopen this automation and edit it again.');
      }
    }
    const now = serverTimestamp();
    const updates = {
      name: automationName(draft.name, current.function_name, triggerId),
      updated_at: now,
      edit_lease: null,
    };
    if (pinTarget) updates.function_version = pinTarget;
    const configChanged = Boolean(pinTarget) || !sameAutomationConfig(automationDraftOf(current), draft);
    if (configChanged) {
      updates.parameters = isPlainObject(draft.parameters) ? draft.parameters : {};
      if (current.trigger_type === 'on_schedule') {
        updates.on_schedule_config = scheduleForSave(draft.on_schedule_config);
      } else {
        updates['on_new_data_config.table_id'] = draft.on_new_data_config?.table_id || null;
      }
      updates.allow_email = false;
      updates.email_permission_updated_at = now;
      updates.email_permission_updated_by = actorUid;
      updates.publish_sweep = deleteField();
      // Failed runs of the previous configuration no longer count.
      updates.run_failures = 0;
    }
    tx.update(ref, updates);
    return { configChanged };
  });
}

// Turn one Automation back on, re-pinned to the version its Function publishes
// NOW. `target` is the caller's `loadRunTarget` read, which supplies the version
// content the parameters are migrated against; the transaction re-reads both the
// head and the automation and refuses rather than enabling something that would
// only be disabled again at its next firing:
//   - the Function is missing, invalid, or has no published version;
//   - the head has moved past the version the caller loaded, so the content in
//     hand no longer describes what would run;
//   - an on-new-data automation watches a table this version does not read.
// Email is never restored here — the user re-allows it with the switch.
export async function enableAutomation(companyId, trigger, target, actorUid) {
  const newBlocks = versionDataSourceBlocks(target?.content);

  const headRef = functionRef(companyId, trigger.function_id);
  const ref = triggerRef(companyId, trigger.id);

  return runTransaction(db, async (tx) => {
    const headSnapshot = await tx.get(headRef);
    const triggerSnapshot = await tx.get(ref);

    if (!headSnapshot.exists()) {
      throw abort('function_missing', 'This function no longer exists.');
    }
    assertResourceAvailable(headSnapshot, 'Function');
    const head = headSnapshot.data() || {};
    if (head.invalid === true) {
      throw abort('function_invalid', head.invalid_reason || 'This function references a resource that no longer exists.');
    }
    const version = head.published_version;
    if (!isPublishedVersion(version)) {
      throw abort('not_published', 'Publish this function before enabling its automations.');
    }
    if (!target?.content || target.publishedVersion !== version) {
      throw abort('version_unavailable', `Version ${version} of this function could not be read. Reopen the Automations tab and try again.`);
    }
    if (!triggerSnapshot.exists()) {
      throw abort('automation_missing', 'This automation no longer exists.');
    }
    await assertAutomationViewAvailable(tx, companyId, triggerSnapshot.data());

    // The version this automation last ran on, read HERE so the filter
    // comparison that decides which saved values carry over is made against
    // the authoritative old contract. If it cannot be read the comparison
    // cannot be made, and the automation is not enabled on an unchecked one.
    const pinned = triggerSnapshot.data()?.function_version;
    let oldBlocks = null;
    if (isPublishedVersion(pinned) && pinned !== version) {
      const oldVersionSnapshot = await tx.get(versionRef(companyId, trigger.function_id, pinned));
      if (!oldVersionSnapshot.exists()) {
        throw abort(
          'old_version_unavailable',
          `Version ${pinned}, which this automation last ran on, could not be read, so its saved filters cannot be carried over. Try again.`,
        );
      }
      oldBlocks = versionDataSourceBlocks(oldVersionSnapshot.data()?.content);
    }

    const current = triggerSnapshot.data() || {};
    if (current.trigger_type === 'on_new_data') {
      const watchedTableId = current.on_new_data_config?.table_id;
      const readTableIds = new Set(
        Object.values(newBlocks).map((block) => block.table_id).filter(Boolean),
      );
      if (!watchedTableId || !readTableIds.has(watchedTableId)) {
        throw abort(
          'table_orphaned',
          `Version ${version} of this function no longer reads the table this automation watches. Choose a table it does read, then enable it.`,
        );
      }
    }

    const now = serverTimestamp();
    const updates = {
      enabled: true,
      disabled_reason: null,
      // A leftover receipt would let a publish prompt from an older publish
      // still claim authority over this automation.
      publish_sweep: deleteField(),
      allow_email: false,
      email_permission_updated_at: now,
      email_permission_updated_by: actorUid,
      updated_at: now,
      // A fresh start for the dispatcher's consecutive-failure count (H53)
      // and for the count of consecutive failed runs.
      dispatch_failures: 0,
      last_dispatch_error: null,
      run_failures: 0,
    };
    if (current.trigger_type === 'on_schedule') {
      updates['on_schedule_config.next_run_at'] = null;
    }

    // A pin that already matches means the saved parameters were written against
    // this exact contract, so they are kept untouched. `dropped` names the
    // filters a migration reset, for the editor to say so (H85).
    const migrated = current.function_version !== version;
    let dropped = [];
    if (migrated) {
      const result = migrateTriggerParameters(current.parameters, newBlocks, oldBlocks);
      updates.parameters = result.parameters;
      updates.function_version = version;
      dropped = result.dropped;
    }

    tx.update(ref, updates);
    return { version, migrated, dropped };
  });
}

// The publish dialog's Keep running commit: ONE transaction, all or
// nothing. Every check is against re-read authoritative documents rather than the
// publish payload, and the sweep receipt is the authority — a human write between
// the publish and this answer clears or predates the receipt, which aborts the
// whole prompt rather than resurrecting an automation somebody deliberately
// touched. On abort nothing is written and the automations stay off, enableable
// one at a time from the tab.
//
// Each kept row carries its sweep token, opening configuration snapshot and
// whether Email was allowed before this publish. These are local review state,
// never new document fields. Email is explicitly off unless the separate Keep
// running with Email action requested its restoration and no filters were lost.
export async function keepAutomationsRunning(
  companyId,
  functionId,
  keptRows,
  expectedVersion,
  restoreEmailIds,
  actorUid,
) {
  const rows = Array.isArray(keptRows) ? keptRows : [];
  if (rows.length === 0) return { applied: true };
  if (!isPublishedVersion(expectedVersion)) return { applied: false, reason: 'version_missing' };

  const restoreEmail = new Set(restoreEmailIds || []);
  const headRef = functionRef(companyId, functionId);
  const contentRef = versionRef(companyId, functionId, expectedVersion);

  return runTransaction(db, async (tx) => {
    // Every read precedes the first write.
    const headSnapshot = await tx.get(headRef);
    const versionSnapshot = await tx.get(contentRef);
    const triggerSnapshots = await Promise.all(
      rows.map((row) => tx.get(triggerRef(companyId, row.id))),
    );

    if (!headSnapshot.exists()) return { applied: false, reason: 'function_missing' };
    assertResourceAvailable(headSnapshot, 'Function');
    const head = headSnapshot.data() || {};
    if (head.invalid === true) return { applied: false, reason: 'function_invalid' };
    if (head.published_version !== expectedVersion) {
      return { applied: false, reason: 'version_advanced' };
    }
    if (!versionSnapshot.exists() || !isPlainObject(versionSnapshot.data()?.content)) {
      return { applied: false, reason: 'version_missing' };
    }

    const changed = triggerSnapshots.some((snapshot, index) => {
      if (!snapshot.exists()) return true;
      const data = snapshot.data() || {};
      return data.function_id !== functionId
        || Boolean(data.managed_by)
        || data.enabled !== false
        || data.allow_email === true
        || data.email_permission_updated_by !== PUBLISH_ACTOR
        || !rows[index].sweep_token
        || data.publish_sweep?.token !== rows[index].sweep_token
        || data.publish_sweep?.version !== expectedVersion
        || !rows[index].expectedConfig
        || stableStringify(automationConfigSnapshot(data)) !== stableStringify(rows[index].expectedConfig);
    });
    if (changed) return { applied: false, reason: 'automation_changed' };

    // The version each automation last ran on is read HERE, from the pin on
    // its own document, so the filter comparison that decides which saved
    // values carry over is made against the authoritative old contract — not
    // a snapshot the dialog took earlier. A pin whose version cannot be read
    // cannot be compared, and nothing is re-enabled on an unchecked contract.
    // (Reads, all of them, before the first write: Firestore's rule.)
    const oldVersionSnapshots = await Promise.all(triggerSnapshots.map((snapshot) => {
      const pin = snapshot.data()?.function_version;
      return isPublishedVersion(pin) && pin !== expectedVersion
        ? tx.get(versionRef(companyId, functionId, pin))
        : Promise.resolve(null);
    }));
    if (oldVersionSnapshots.some((snapshot) => !snapshot?.exists() || !isPlainObject(snapshot.data()?.content))) {
      return { applied: false, reason: 'old_version_missing' };
    }

    const newVersionBlocks = versionDataSourceBlocks(versionSnapshot.data()?.content);
    const migrations = triggerSnapshots.map((snapshot, index) => migrateTriggerParameters(
      snapshot.data()?.parameters,
      newVersionBlocks,
      versionDataSourceBlocks(oldVersionSnapshots[index].data()?.content),
    ));
    const readTableIds = new Set(Object.values(newVersionBlocks).map((block) => block.table_id));
    if (triggerSnapshots.some((snapshot) => {
      const data = snapshot.data() || {};
      return data.trigger_type === 'on_new_data'
        && !readTableIds.has(data.on_new_data_config?.table_id);
    })) {
      return { applied: false, reason: 'table_orphaned' };
    }
    if (rows.some((row, index) => restoreEmail.has(row.id)
      && (row.hadEmail !== true || migrations[index].dropped.length > 0))) {
      return { applied: false, reason: 'email_review_changed' };
    }

    const now = serverTimestamp();
    triggerSnapshots.forEach((snapshot, index) => {
      const row = rows[index];
      const data = snapshot.data() || {};
      const updates = {
        enabled: true,
        disabled_reason: null,
        function_version: expectedVersion,
        parameters: migrations[index].parameters,
        allow_email: restoreEmail.has(row.id),
        email_permission_updated_at: now,
        email_permission_updated_by: actorUid,
        publish_sweep: deleteField(),
        updated_at: now,
        dispatch_failures: 0,
        last_dispatch_error: null,
        run_failures: 0,
      };
      if (data.trigger_type === 'on_schedule') {
        updates['on_schedule_config.next_run_at'] = null;
      }
      tx.update(snapshot.ref, updates);
    });

    return { applied: true };
  });
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const twoDigits = (value) => String(value).padStart(2, '0');

const ordinal = (day) => {
  const number = Number.isInteger(day) ? day : 1;
  const tens = number % 100;
  if (tens >= 11 && tens <= 13) return `${number}th`;
  const units = number % 10;
  if (units === 1) return `${number}st`;
  if (units === 2) return `${number}nd`;
  if (units === 3) return `${number}rd`;
  return `${number}th`;
};

// When an Automation runs, including its configured timezone. Watched tables are named
// through the caller's `tableNameById`, which falls back to the id.
export function describeWhen(trigger, tableNameById = {}) {
  if (trigger?.trigger_type === 'on_new_data') {
    const tableId = trigger.on_new_data_config?.table_id;
    if (!tableId) return 'On new data · no table';
    return `On new data · ${tableNameById[tableId] || tableId}`;
  }
  const config = trigger?.on_schedule_config || {};
  const time = `${twoDigits(config.hour ?? 9)}:${twoDigits(config.minute ?? 0)} ${config.timezone || 'timezone not set'}`;
  if (config.frequency === 'weekly') {
    return `Weekly ${WEEKDAY_LABELS[config.day_of_week ?? 0] || WEEKDAY_LABELS[0]} ${time}`;
  }
  if (config.frequency === 'monthly') {
    return `Monthly ${ordinal(config.day_of_month ?? 1)} ${time}`;
  }
  return `Daily ${time}`;
}
